// Concentration + contribution helpers for the portfolio monitoring screens.
// Pure: group holdings across accounts by security, rank by exposure and by
// unrealised gain/loss. Money is in DOLLARS (matches the PortfolioSnapshot).

export interface ExposureInput {
  code: string;
  name?: string | null;
  assetClass?: string | null;
  accountName?: string | null;
  units?: number;
  currentValue: number;       // dollars
  costBase?: number;          // dollars
  unrealisedGainLoss?: number; // dollars
}

export interface ExposureRow {
  code: string;
  name: string | null;
  assetClass: string | null;   // 'Mixed' if it differs across accounts
  value: number;              // grouped dollars
  exposurePct: number;        // value / portfolioValue, 0..1
  gainLoss: number;           // grouped dollars
  returnPct: number;          // gainLoss / costBase, 0..1 (0 if no cost base)
  units: number;              // grouped units
  costBase: number;           // grouped dollars
  currentPrice: number;       // value / units (0 if units 0)
  accountNames: string[];     // distinct accounts the security is held in
}

/** Group flat holdings (possibly the same security across accounts) by code. */
export function groupHoldings(holdings: ExposureInput[], portfolioValue: number): ExposureRow[] {
  interface Acc { code: string; name: string | null; classes: Set<string>; value: number; gainLoss: number; cost: number; units: number; accounts: Set<string>; }
  const byCode = new Map<string, Acc>();
  for (const h of holdings) {
    const cur = byCode.get(h.code) ?? { code: h.code, name: h.name ?? null, classes: new Set<string>(), value: 0, gainLoss: 0, cost: 0, units: 0, accounts: new Set<string>() };
    cur.value += h.currentValue;
    cur.gainLoss += h.unrealisedGainLoss ?? 0;
    cur.cost += h.costBase ?? 0;
    cur.units += h.units ?? 0;
    if (h.assetClass) cur.classes.add(h.assetClass);
    if (h.accountName) cur.accounts.add(h.accountName);
    if (!cur.name && h.name) cur.name = h.name;
    byCode.set(h.code, cur);
  }
  return [...byCode.values()].map((r) => ({
    code: r.code,
    name: r.name,
    assetClass: r.classes.size === 0 ? null : r.classes.size === 1 ? [...r.classes][0] : 'Mixed',
    value: round2(r.value),
    exposurePct: portfolioValue > 0 ? round4(r.value / portfolioValue) : 0,
    gainLoss: round2(r.gainLoss),
    returnPct: r.cost > 0 ? round4(r.gainLoss / r.cost) : 0,
    units: r.units,
    costBase: round2(r.cost),
    currentPrice: r.units > 0 ? round2(r.value / r.units) : 0,
    accountNames: [...r.accounts],
  }));
}

/** Top N positions by current value. */
export function topExposures(holdings: ExposureInput[], portfolioValue: number, n = 5): ExposureRow[] {
  return groupHoldings(holdings, portfolioValue).sort((a, b) => b.value - a.value).slice(0, n);
}

/** Top N losing positions (most negative unrealised gain/loss). Only negatives. */
export function largestDetractors(holdings: ExposureInput[], portfolioValue: number, n = 5): ExposureRow[] {
  return groupHoldings(holdings, portfolioValue)
    .filter((r) => r.gainLoss < 0)
    .sort((a, b) => a.gainLoss - b.gainLoss)
    .slice(0, n);
}

function round2(n: number) { return Math.round(n * 100) / 100; }
function round4(n: number) { return Math.round(n * 10000) / 10000; }
